import _jsx from "@babel/runtime/helpers/jsx";
import PropTypes from 'prop-types';
import React, { useEffect, useState, memo, useRef, useId } from 'react';
import clsx from 'clsx';
import hasDom from 'has-dom';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';
import CheckboxGroup from '../CheckboxGroup/index.js';
import ErrorMessages from '../ErrorMessages/index.js';
const renderSelectLabel = _ref => {
  let {
    selectedOptions = [],
    defaultLabel = 'Select'
  } = _ref;
  const {
    length
  } = selectedOptions;
  const firstOptionLabel = _get(selectedOptions, '[0].label', '');
  const firstMultiLabel = _get(selectedOptions, '[0].label.props.children[0].props.label', '');
  const firstLabel = typeof firstOptionLabel === 'object' ? _get(firstOptionLabel, 'props.label', '') : firstOptionLabel;
  const label = firstMultiLabel || firstLabel;
  if (length === 0) {
    return defaultLabel;
  }
  return `${label} ${length > 1 ? '(+' + (length - 1) + ')' : ''}`;
};
const MultiSelectDropdown = /*#__PURE__*/memo(_ref2 => {
  let {
    className,
    disabled,
    label,
    defaultSelectLabel = 'Select',
    affixLabel,
    options = [],
    onChange = () => {},
    renderSelectLabel: renderCustomizedSelectLabel,
    errors,
    help,
    uiContext,
    ...rest
  } = _ref2;
  const renderSelectBoxLabel = renderCustomizedSelectLabel && typeof renderCustomizedSelectLabel === 'function' ? renderCustomizedSelectLabel : renderSelectLabel;
  const initialSelectedOptions = options.filter(_ref3 => {
    let {
      selected
    } = _ref3;
    return selected;
  });
  const initialSelectLabel = renderSelectBoxLabel({
    selectedOptions: initialSelectedOptions,
    defaultLabel: defaultSelectLabel
  });
  const [selectLabel, setSelectLabel] = useState(initialSelectLabel);
  const [selectedOptions, setSelectedOptions] = useState(initialSelectedOptions);
  const [showOptions, setShowOptions] = useState(false);
  const selectWrapperRef = useRef();
  const checkOptionClick = event => {
    const clickedOutside = _get(selectWrapperRef, 'current.contains') && !selectWrapperRef.current.contains(event.target);
    if (showOptions && clickedOutside) {
      setShowOptions(false);
    }
  };

  // update the selectedOptions state when options props is changed
  useEffect(() => {
    // only update when the multiple select box is hide to keep UI smoother when user interacts with the SelectDropdown
    if (!showOptions) {
      const newSelectedOptions = options.filter(_ref4 => {
        let {
          selected
        } = _ref4;
        return selected;
      });
      if (newSelectedOptions.length !== selectedOptions.length || !_isEqual(newSelectedOptions, selectedOptions)) {
        const newSelectLabel = renderSelectBoxLabel({
          selectedOptions: newSelectedOptions,
          defaultLabel: defaultSelectLabel
        });
        setSelectedOptions(newSelectedOptions);
        setSelectLabel(newSelectLabel);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, showOptions]);
  useEffect(() => {
    if (hasDom()) {
      if (showOptions) {
        // detect click events only when the dropdown is open
        // so we can auto-close the dropdown when user clicks outside of it
        window.addEventListener('click', checkOptionClick);
      } else {
        window.removeEventListener('click', checkOptionClick);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOptions]);

  // make sure we clean up handler when component is unmounted
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => window.removeEventListener('click', checkOptionClick), []);

  // use react18 useId to generate unique id
  const id = useId();
  const handleChange = event => {
    const {
      target: {
        value: targetValue,
        checked: targetIsChecked
      }
    } = event;
    const option = options.filter(_ref5 => {
      let {
        value
      } = _ref5;
      return value === targetValue;
    })[0];
    if (option && option.value) {
      const updatedSelection = [...selectedOptions];
      if (targetIsChecked) {
        // if checked, add to the array
        updatedSelection.splice(selectedOptions.length, 0, option);
      } else {
        // remove unchecked item from the array
        updatedSelection.splice(selectedOptions.findIndex(_ref6 => {
          let {
            value
          } = _ref6;
          return value === option.value;
        }), 1);
      }

      // update selected options
      setSelectedOptions(updatedSelection);

      // Update the label in select
      const updatedSelectLabel = renderSelectBoxLabel({
        selectedOptions: updatedSelection,
        defaultLabel: defaultSelectLabel
      });
      setSelectLabel(updatedSelectLabel);

      // trigger onChange
      const selectedValues = updatedSelection.map(_ref7 => {
        let {
          value
        } = _ref7;
        return value;
      });
      if (uiContext !== 'filters') {
        onChange(selectedValues);
      } else {
        onChange(event);
      }
    }
  };
  const handleSelectClick = () => {
    setShowOptions(!showOptions);
  };
  const selectedValues = selectedOptions.map(_ref8 => {
    let {
      value
    } = _ref8;
    return value;
  });
  return /*#__PURE__*/_jsx("div", {
    "data-cmp": "multiselect",
    className: clsx('multiselect-dropdown form-group', className)
  }, void 0, label && /*#__PURE__*/_jsx("label", {
    htmlFor: id,
    className: "control-label"
  }, void 0, label), /*#__PURE__*/React.createElement("div", {
    className: "multiselect-wrapper",
    ref: selectWrapperRef
  }, /*#__PURE__*/_jsx("div", {
    className: clsx('multiselect-box form-control cursor-pointer text-size-300 display-flex justify-content-between align-items-center', {
      open: showOptions,
      tall: affixLabel,
      disabled
    }),
    onClick: handleSelectClick,
    role: "button",
    tabIndex: "0"
  }, void 0, affixLabel && /*#__PURE__*/_jsx("div", {}, void 0, /*#__PURE__*/_jsx("div", {
    className: "margin-horizontal-1 text-size-200"
  }, void 0, affixLabel), /*#__PURE__*/_jsx("div", {
    className: "margin-horizontal-1 text-bold"
  }, void 0, selectLabel)), !affixLabel && selectLabel), /*#__PURE__*/_jsx("div", {
    className: clsx('multiselect-options', {
      open: showOptions,
      'display-block': showOptions,
      'display-none': !showOptions
    })
  }, void 0, /*#__PURE__*/_jsx("div", {
    className: "form-control padding-vertical-2 padding-horizontal-4"
  }, void 0, /*#__PURE__*/_jsx(CheckboxGroup, {
    options: options,
    value: selectedValues,
    onChange: handleChange
  })))), help && /*#__PURE__*/_jsx("span", {
    className: "help-block"
  }, void 0, help), errors && /*#__PURE__*/_jsx(ErrorMessages, {
    errors: errors
  }));
});
export default MultiSelectDropdown;